import { Edit3, MapPin, Trash2, User2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import { CurrentLocation } from "../../components/ui/dashboard/StatusCommon";
import InfoItem from "../../components/ui/Info";
import SkeletonForDetailsPage from "../../components/ui/SkeletonForDetailsPage";
import { buildApiUrl, formatDate } from "../../lib/utils";
import DeleteUserModal from "../user/ui/DeleteUserModal";
import VehicleAssignmentHistory from "../vehicle/refactor/Vehicleassignmenthistory";
import FinanceTab from "../vehicle/refactor/vehicleDetails/FinanceTab";
import TripHistoryTab from "../vehicle/refactor/vehicleDetails/TripHistoryTab";
import DriverFinanceCard02 from "./components/DriverFinanceCard02";
import { useGetDriverByIdQuery } from "./driverApiSlice";
import DriverModel from "./DriverModal";
import IncidentAndAccidentTab from "./refactor/driverDetails/IncidentAndAccidentTab";

const DriverDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Finances");
  const [openModal, setOpenModal] = useState("");

  const { data, isLoading, isError, refetch } = useGetDriverByIdQuery(
    id || "",
    {
      skip: !id,
    },
  );

  if (!id) {
    return <></>;
  }

  // Loading skeleton
  if (isLoading) {
    return <SkeletonForDetailsPage />;
  }

  if (isError || (!isLoading && !data)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Error loading driver details</div>
      </div>
    );
  }

  const driver = data?.data;

  if (!driver) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Driver not found</div>
      </div>
    );
  }

  const tabs = [
    "Finances",
    "Trip information",
    "Incident & Accident history",
    "Vehicle assignment history",
  ];

  const handleModal = () => {
    setOpenModal("");
    refetch();
  };

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6 overflow-hidden">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <button
            onClick={() => navigate("/admin/drivers")}
            className="hover:text-gray-800"
          >
            All drivers
          </button>
          <span>›</span>
          <span className="text-gray-800 font-medium">
            {driver?.fullName || "Driver Details"}
          </span>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Driver Profile Section */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-12 bg-white rounded-[10px] p-4 text-[16px]">
            <div className="flex items-center space-x-10">
              {driver?.imageUrl ? (
                <img
                  src={buildApiUrl(driver?.imageUrl)}
                  alt={driver?.fullName}
                  className="w-[8rem] h-[8rem] rounded-[10px] object-cover border border-gray-300 shadow-sm"
                />
              ) : (
                <div className="w-[8rem] h-[8rem] rounded-[10px] bg-gray-300 p-6">
                  <User2 className="w-full h-full" />
                </div>
              )}
            </div>

            <div className="w-full space-y-4">
              <div className="w-full flex flex-col gap-4 lg:flex-row lg:items-center justify-between">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="font-semibold">{driver?.fullName}</h1>
                    <CurrentLocation location={driver?.currentLocation} />
                  </div>
                  <h1 className="text-[13px]">{driver.email}</h1>
                </div>
                <div className="flex flex-wrap gap-x-8 gap-y-3">
                  <InfoItem label="Phone no." value={driver.phoneNumber} />
                  <InfoItem
                    label="Assigned vehicle"
                    value={driver.currentVehicle}
                  />
                  <InfoItem
                    label="Vehicle type"
                    value={driver.currentVehicleType}
                  />
                  <InfoItem
                    label="License plate"
                    value={formatDate(driver.lastTripDate)}
                  />
                  <InfoItem
                    label="License Exp. date"
                    value={formatDate(driver.licenseExpiryDate)}
                  />
                </div>
              </div>

              <div className="h-[2px] w-full bg-gray-200"></div>

              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex flex-wrap gap-2">
                  <AppButton
                    onClick={() => {}}
                    className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
                  >
                    Track
                    <MapPin className="h-4 w-4 ml-2" />
                  </AppButton>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {}}
                    className="flex items-center px-4 py-2 border border-pColor text-pColor rounded-lg h-[38px] text-sm"
                  >
                    Change status
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      className="h-6 w-6 ml-2"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="m7 10l5 5l5-5"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setOpenModal("edit")}
                    className="flex items-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/60 hover:text-white transition-colors h-[38px] text-sm"
                  >
                    Edit driver
                    <Edit3 className="h-4 w-4 ml-2" />
                  </button>
                  <AppButton
                    onClick={() => setOpenModal("delete")}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-800 transition-colors h-[38px] text-sm"
                  >
                    Delete
                    <Trash2 className="h-4 w-4 ml-2" />
                  </AppButton>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          {/* <DriverDetailsCard driver={driver} /> */}

          <DriverFinanceCard02 driver={driver} />

          {/* Tabs */}
          <div className="bg-white rounded-[10px] p-2 mt-5 h-[70px] flex items-center overflow-x-auto">
            <nav className="flex px-2 whitespace-nowrap min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1 mr-4 font-medium text-[16px] transition-colors ${
                    activeTab === tab
                      ? "bg-primary/20 text-primary rounded-[8px]"
                      : "border-transparent text-pColor hover:text-hColor"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/* Trip History Table */}
          {activeTab === "Finances" && <FinanceTab id={id} idType="driver" />}

          {activeTab === "Trip information" && <TripHistoryTab driverId={id} />}

          {activeTab === "Incident & Accident history" && (
            <IncidentAndAccidentTab driverId={id} />
          )}

          {activeTab === "Vehicle assignment history" && (
            <VehicleAssignmentHistory id={id} idType="driverId" />
          )}
        </div>
      </div>
      <DriverModel
        isOpen={openModal === "edit"}
        onClose={handleModal}
        driver={driver}
      />
      <DeleteUserModal
        isOpen={openModal === "delete"}
        onClose={handleModal}
        userId={driver.userId}
        entityLabel="driver"
        redirectTo="/admin/drivers"
      />
    </div>
  );
};

export default DriverDetails;
